import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg';
import { ReactComponent as IconInfoBusiness } from "@/assets/svgs/my-info-business.svg";
import Button from "@/components/Button";
import Modal from "@/dsComponents/Modal";
import googleKeys from "@/constants/configuration/google-keys.json";
import { useHciImprovement } from "@/hooks/use-hci-improvement";
import useModal from '@/hooks/use-modal';
import useMyInfo from "@/hooks/use-my-info/index";
import { NavLink, useQuery } from "@/lib/router-dom";
import API_CONFIG from '@/services/api/config.js';
import API_LOGIN from "@/services/api/login";
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import { sanitizePitstopName } from "@/utils/validate-pitstop";
import { uniqueId } from 'lodash';
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import CompanyInfo from "./components/company-info";
import MyinfoBusinessCard from "./components/my-info-business";
import PersonalInfo from "./components/personal-info";

import API_ENROLL from "@/services/api/myenrol.js";
import AppointmentRadioGroup from '@/dsComponents/radio/AppointmentRadioGroup';
import useToast from "@/hooks/use-toast";
import Portal from '@/components/Portal/index';
import { Box, ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight, ModalHeader, ModalTitle } from '@dextech-ai/dex-design-system';

let baseUrl = window.location.origin

// Shell-hosted builds serve admin-ui under PUBLIC_URL (e.g. `/admin`); standalone builds leave it
// empty. The MyInfo redirect_uri / callbackUrl must point at the real signup path
// (`…/admin/sign-up` under the unified portal) so CorpPass returns the browser to the admin SPA
// rather than the portal. The authorise redirect_uri and the token-exchange callbackUrl must match
// exactly, so build the value once here and reuse it in all three places.
const signupPath = `${process.env.PUBLIC_URL || ''}/sign-up`
const signupCallbackUrl = `${baseUrl}${signupPath}`

const BuildexSignup = ({
	register,
	errors,
	trigger,
	isValid,
	loading,
	setValue,
	watch,
	handleSubmit,
	loginHandler,
	control,
	reset
}) => {

	const { env, dex } = getEnvAndDex();
	const isHciEnabled = useHciImprovement();

	const defaultCaptchaValue = googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY;
	const [captchaToken, setCaptchaToken] = useState(!defaultCaptchaValue);
	const [pitstopHost, setPitstopHost] = useState("");
	const [defaultDomain, setDefaultDomain] = useState("");
	const [errorMessage, setErrorMessage] = useState();

	const [myInfoBizAppointmentList, setMyInfoBizAppointmentList] = useState([]);
	const [isFilledWithMyInfo, setIsFilledWithMyInfo] = useState(false)
	const [isLoadingMyInfo, setIsLoadingMyInfo] = useState(false)
	const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
	const [appointmentIdSelected, setAppointmentIdSelected] = useState("");
	const [currentMyInfoProfile, setCurrentMyInfoProfile] = useState({
		email: "",
		fullName: "",
		mobileNumber: "",
		organization: {}
	})

	const {	visible: visibleAppointmentSelection, show: showAppointmentSelection, hide: hideAppointmentSelection} = useModal();
	const { fetch: fetchMyInfo, currentItem: myInfoItem } = useMyInfo();
	const { visible: isShowMyInfoModal, show: showMyInfoConfimModal, hide: hideMyInfoConfirmModal } = useModal();
	const { addToastMessage } = useToast();
	const captchaRef = useRef(null);
	const stateID = uniqueId();
	const query = useQuery();
	const authCodeQueryparams = query.get("code");

	useEffect(() => {
		const fetchConfig = async () => {
			const defaultHost = (await API_CONFIG.index())[1];
			setPitstopHost(defaultHost?.pitstopHost);
			setDefaultDomain(defaultHost?.pitstopHost?.split('{{SystemShortName}}')[1]);
		}

		fetchConfig();
		fetchMyInfo();
	}, [])

	useEffect(() => {
		async function getData() {
			window.history.replaceState(null, "", signupPath);
			getEntityData()
		}
		if (authCodeQueryparams !== null) {
			getData()
		}
	}, [authCodeQueryparams])

	const infoItems = useMemo(() => {
		if (myInfoItem) {
			return {
				countryOfIncorp: myInfoItem.countryOfIncorp?.map(item => ({id: item.value, label: item.name})),
				authRedirectUrl: myInfoItem?.authRedirectUrl,
			}
		}
	}, [myInfoItem])


	async function handleChangeReCaptcha(value) {
		const [error] = await API_LOGIN.siteVerify({ token: value });
		if (error) {
			setCaptchaToken(false);
			if (captchaRef.current) {
				captchaRef.current.reset();
			}
			return setErrorMessage("reCAPTCHA verification failed")
		}
		setCaptchaToken(true);
	}

	const setFormValue = (key, value) => {
		setValue(key, value ?? "", { shouldValidate: true });
	};

	const getEntityData = async () => {
		setIsLoadingMyInfo(true);
		const params = "authCode=" + authCodeQueryparams + `&callbackUrl=${signupCallbackUrl}`;
		const [error, result] = await API_ENROLL.getRequestParams(params);
		setIsLoadingMyInfo(false)
		if(error) {
			return addToastMessage(error?.message, "error");
		}
		setIsFilledWithMyInfo(true);
		let infoBizData = {};
		Object.keys(result).forEach((key) => {
			infoBizData[key] = result[key];
		});
		const appointmentArray = result.entity["appointments"]["appointments-list"];
		const appointments = appointmentArray.filter(item => item["person-reference"]?.idno)
		setMyInfoBizAppointmentList(appointments);
		fillMyInfoDetails(infoBizData, appointments);
	}

	const fillMyInfoDetails = (infoBizData, appointmentArray) => {

		// Fill form details
		const basic_profile = infoBizData.entity["basic-profile"];
		const company = basic_profile["entity-name"].value;
		const uen = basic_profile.uen.value;

		const countryOfIncorporation =  basic_profile["country-of-incorporation"]

		setFormValue("company", company)
		setFormValue("countryOfIncorporation", countryOfIncorporation?.code)
		setFormValue("uen", uen)

		setCurrentMyInfoProfile(prev => ({
			...prev,
			fullName: infoBizData.person?.name.value,
			organization: {
				...prev.organization,
				name: basic_profile["entity-name"].value,
				uen: basic_profile.uen.value,
				countryOfIncorporation: countryOfIncorporation,
			}
		}))
		if (appointmentArray.length > 1) {
			showAppointmentSelection();
		}
	}

	const onValidSubmit = async (data) => {
		if (defaultCaptchaValue && !captchaToken) {
			setErrorMessage("Please complete the reCAPTCHA verification.");
			return;
		}
		setErrorMessage(undefined);
		await submitHandler(data);
	};

	const onInvalidSubmit = () => {
		setHasAttemptedSubmit(true);
	};

	const submitHandler = async (data) => {

		const payload = {
			email: data.email.toLowerCase(),
			firstName: data.firstName,
			lastName: data.lastName,
			mobileNumber: null,
			org: {
				pitstopName: sanitizePitstopName(data.company),
				pitstopUrl: (pitstopHost?.split('{{SystemShortName}}')[0] + data.pitstopUrl + pitstopHost?.split('{{SystemShortName}}')[1]).toLowerCase(),
				pitstopShortName: data.pitstopUrl?.trim().toLowerCase(),
				name: data.company,
				uen: data.uen,
				// Both keys are omitted entirely when HCI is gated off, so the
				// payload matches the pre-HCI shape the backend still expects.
				...(isHciEnabled
					? {
						hciCode: data.entityType === 'NON_HCI' ? null : (data.hciCode || null),
						entityType: data.entityType || 'HCI'
					}
					: {}),
				companyType: null,
				incorporationDate: null,
				countryofIncorporation: data.countryOfIncorporation || null,
				registeredAddress: null,
				appointmentName: null,
				appointmentNationality: null,
				appointmentEmail: null,
				appointmentMobile: null,
				enrolmentDataSource: isFilledWithMyInfo ? "MYINFO" : "MANUAL",
				appointmentPosition: null,
				appointmentIdNumber: appointmentIdSelected || null
			},
		};

		loginHandler(payload)
	}

	function getStartedMyInfo() {
		const redirect_uri = myInfoItem?.authRedirectUrl + `&purpose=MyEnrolmentForm&state=${stateID}&redirect_uri=${signupCallbackUrl}`;
		window.location = redirect_uri;
	}

	const onResetFormHandler = () => {
		reset({
			countryOfIncorporation: "",
		});
		setCurrentMyInfoProfile({
			email: "",
			fullName: "",
			mobileNumber: "",
			organization: {}
		})
		setIsFilledWithMyInfo(false);
		setAppointmentIdSelected("");
	}

	return (
		<div className="layout-login__right layout-login__right-buildex">
			<div className="login-box-buildex width-full">
				<div
					className="login-box__title-buildex--header-1"
					data-testid="page-title"
				>
					Sign Up
				</div>
				<div className="login-box__content-buildex">
					<MyinfoBusinessCard
						onMyinfoBusinessClick={showMyInfoConfimModal}
						isFilledWithMyInfo={isFilledWithMyInfo}
						clearForm={onResetFormHandler}
					/>
					<div className="enrolment-form__info-flex">
						<hr />
						<h3 className="enrolment-form__padding">OR</h3>
						<hr />
					</div>
					<form
						className="login-box__form"
						onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
						role="form"
					>
						<div className="login-box__form__cols">
							<PersonalInfo
								errors={errors}
								register={register}
								setValue={setValue}
							/>
							<hr />
							<CompanyInfo
								errors={errors}
								register={register}
								watch={watch}
								setValue={setValue}
								infoItems={infoItems}
								defaultDomain={defaultDomain}
								control={control}
								isFilledWithMyInfo={isFilledWithMyInfo}
								currentProfile={currentMyInfoProfile}
								trigger={trigger}
							/>

							{isValid && googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY && (
								<ReCAPTCHA
									sitekey={`${googleKeys?.[dex]?.[env]?.CAPTCHA_SITE_KEY}`}
									ref={captchaRef}
									style={{ marginTop: "20px" }}
									onChange={(value) =>
										handleChangeReCaptcha(value)
									}
									onExpired={() => setCaptchaToken(false)}
									size="normal"
									data-testid="recaptcha-test"
								/>
							)}
							{errorMessage && (
								<div className="login-box__form__col--error">
									<Error />{" "}
									<span className="t--required">
										{errorMessage}
									</span>
								</div>
							)}
							<div className="login-box__mandatory-field-text">
								<span>*</span> Mandatory fields.
							</div>
							<div>
								<Box mt={6} mb={10}>
									<Button
										type="submit"
										id="sign-up-submit-trigger-id"
										full
										round
										value="Submit"
										maxWidth="250"
										text="Sign Up"
										disabled={loading}
										loading={loading}
										data-testid="sign-up-submit"
									/>

									{hasAttemptedSubmit && !isValid && (
										<div
											className="login-box__form__col--error--form-validation-summary"
											data-testid="form-validation-summary"
										>
											<span className="t--required">
												Please check the form and correct all errors before submitting.
											</span>
										</div>
									)}

									<Box mt={6}>
										Have an account?{" "}
										<NavLink className="link" to="/login">
											Sign in instead.
										</NavLink>{" "}
									</Box>
								</Box>
							</div>
						</div>
					</form>
				</div>
			</div>

			<Portal id="app-blocker">
				{isLoadingMyInfo && (
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/loading.gif")} alt="Loading" />
						</div>
					</div>
				)}
			</Portal>

			<Modal
				visible={isShowMyInfoModal}
				close={hideMyInfoConfirmModal}
				dismissClickOutsideModal={true}
			>
				<ModalHeader>
					<ModalTitle>Myinfo business</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<IconInfoBusiness />
					<div>
						Pre-fill the enrolment form quickly with Myinfo
						business.
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							ghost
							data-testid="cancel-button"
							noborder
							submit={hideMyInfoConfirmModal}
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button
							full
							maxWidth="140"
							text="Get Started"
							submit={getStartedMyInfo}
						/>
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal
				visible={visibleAppointmentSelection}
				close={hideAppointmentSelection}
				dismissClickOutsideModal={false}
			>
				<ModalHeader>
					<ModalTitle>Select Appointment</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="appointment-modal_height">
						<AppointmentRadioGroup appointments={myInfoBizAppointmentList} selectedId={appointmentIdSelected} onSelect={setAppointmentIdSelected} />
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							ghost
							data-testid="cancel-button"
							noborder
							submit={hideAppointmentSelection}
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button
							full
							maxWidth="140"
							text="Confirm"
							submit={hideAppointmentSelection}
						/>
					</ModalFooterRight>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default BuildexSignup;

BuildexSignup.propTypes = {
	errors: PropTypes.objectOf(
		PropTypes.shape({
			message: PropTypes.string,
		})
	).isRequired,
	register: PropTypes.func.isRequired,
	trigger: PropTypes.func.isRequired,
	setValue: PropTypes.func.isRequired,
	watch: PropTypes.func.isRequired,
	isValid: PropTypes.bool.isRequired,
	loading: PropTypes.bool.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	loginHandler: PropTypes.func.isRequired,
	control: PropTypes.object.isRequired,
	reset: PropTypes.func
};

